import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Home, Mail, Lock, User, AlertCircle, Loader2, Building2 } from 'lucide-react';
import { trackEvent } from '../lib/analytics';
import { getDefaultRouteForRole } from '../lib/roleContext';
import { getPostAuthRedirect } from '../lib/emailVerificationFlow';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const { t } = useLanguage();

  const defaultRole = searchParams.get('role') === 'owner' ? 'OWNER' : 'USER';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: defaultRole,
    acceptTerms: false,
    acceptPrivacy: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    trackEvent(
      defaultRole === 'OWNER' ? 'owner_registration_started' : 'user_registration_started',
      { source: 'register_page' },
    );
  }, [defaultRole]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.acceptTerms) {
      setError(t('legal.must_accept_terms', 'You must accept the Terms of Service to create an account'));
      return;
    }

    if (!formData.acceptPrivacy) {
      setError(t('legal.must_accept_privacy', 'You must accept the Privacy Policy to create an account'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('register.passwords_dont_match', 'Passwords do not match'));
      return;
    }

    if (formData.password.length < 8) {
      setError(t('register.password_too_short', 'Password must be at least 8 characters'));
      return;
    }

    setLoading(true);

    try {
      const userData = await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        accepted_terms_at: new Date().toISOString(),
        accepted_privacy_at: new Date().toISOString(),
      });
      trackEvent(
        formData.role === 'OWNER' ? 'owner_registration_completed' : 'user_registration_completed',
        { source: 'register_page' },
      );
      // Unverified accounts go to the pending page; verified accounts keep the
      // normal role-based destination.
      navigate(getPostAuthRedirect(userData, getDefaultRouteForRole(formData.role)));
    } catch (err) {
      setError(
        err.response?.data?.detail
          || t('register.registration_failed', 'Registration failed. Please try again.'),
      );
    } finally {
      setLoading(false);
    }
  };

  const submitLabel = formData.role === 'OWNER'
    ? t('register.submit_owner', 'Create Owner Account')
    : t('register.submit_renter', 'Create Renter Account');

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4 py-12" data-testid="register-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 bg-signal-blue rounded-sm flex items-center justify-center">
            <Home className="w-6 h-6 text-white" />
          </div>
          <span className="font-heading text-2xl font-bold text-slate-800">
            TLA<span className="text-military-gold">HOUSING</span>
          </span>
        </Link>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          <div className="text-center mb-6">
            <h1 className="font-heading text-2xl font-bold text-slate-800 mb-2">
              {t('register.create_account', 'CREATE ACCOUNT')}
            </h1>
            <p className="text-slate-600 text-sm">
              {t('register.join_subtitle', 'Join TLA Housing Europe today')}
            </p>
          </div>

          <div className="flex gap-2 mb-6">
            <button
              type="button"
              onClick={() => setFormData({ ...formData, role: 'USER' })}
              className={`flex-1 py-3 px-4 rounded-sm border text-sm font-medium transition-all ${
                formData.role === 'USER'
                  ? 'bg-signal-blue/10 border-signal-blue text-signal-blue'
                  : 'bg-white border-slate-300 text-slate-600 hover:border-slate-600'
              }`}
              data-testid="role-user"
            >
              <User className="w-4 h-4 mx-auto mb-1" />
              {t('register.role_renter', 'Renter')}
            </button>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, role: 'OWNER' })}
              className={`flex-1 py-3 px-4 rounded-sm border text-sm font-medium transition-all ${
                formData.role === 'OWNER'
                  ? 'bg-military-gold/10 border-military-gold text-military-gold'
                  : 'bg-white border-slate-300 text-slate-600 hover:border-slate-600'
              }`}
              data-testid="role-owner"
            >
              <Building2 className="w-4 h-4 mx-auto mb-1" />
              {t('register.role_owner', 'Owner')}
            </button>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-sm mb-6">
              <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-slate-700 text-sm">
                {t('register.full_name', 'Full Name')}
              </Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-800" />
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('register.name_placeholder', 'John Smith')}
                  required
                  className="pl-10 bg-white border-slate-300 text-slate-800 placeholder:text-slate-800 focus:border-signal-blue"
                  data-testid="register-name"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-slate-700 text-sm">
                {t('auth.email_address', 'Email Address')}
              </Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-800" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('register.email_placeholder', 'you@example.com')}
                  required
                  className="pl-10 bg-white border-slate-300 text-slate-800 placeholder:text-slate-800 focus:border-signal-blue"
                  data-testid="register-email"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700 text-sm">
                {t('auth.password', 'Password')}
              </Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-800" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={t('register.password_min_placeholder', 'Min. 8 characters')}
                  required
                  className="pl-10 bg-white border-slate-300 text-slate-800 placeholder:text-slate-800 focus:border-signal-blue"
                  data-testid="register-password"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-slate-700 text-sm">
                {t('register.confirm_password', 'Confirm Password')}
              </Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-800" />
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder={t('register.confirm_password_placeholder', '••••••••')}
                  required
                  className="pl-10 bg-white border-slate-300 text-slate-800 placeholder:text-slate-800 focus:border-signal-blue"
                  data-testid="register-confirm-password"
                />
              </div>
            </div>

            <div className="space-y-3 pt-2">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="acceptTerms"
                  checked={formData.acceptTerms}
                  onCheckedChange={(checked) => setFormData({ ...formData, acceptTerms: checked })}
                  className="mt-1 border-slate-600 data-[state=checked]:bg-signal-blue data-[state=checked]:border-signal-blue"
                  data-testid="accept-terms"
                />
                <div className="flex-1">
                  <Label htmlFor="acceptTerms" className="text-slate-700 text-sm cursor-pointer">
                    {t('register.accept_terms_prefix', 'I have read and accept the')}{' '}
                    <Link to="/terms" target="_blank" className="text-signal-blue hover:underline">
                      {t('register.terms_link', 'Terms of Service (AGB)')}
                    </Link>
                    <span className="text-red-400">*</span>
                  </Label>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="acceptPrivacy"
                  checked={formData.acceptPrivacy}
                  onCheckedChange={(checked) => setFormData({ ...formData, acceptPrivacy: checked })}
                  className="mt-1 border-slate-600 data-[state=checked]:bg-signal-blue data-[state=checked]:border-signal-blue"
                  data-testid="accept-privacy"
                />
                <div className="flex-1">
                  <Label htmlFor="acceptPrivacy" className="text-slate-700 text-sm cursor-pointer">
                    {t('register.accept_privacy_prefix', 'I have read and accept the')}{' '}
                    <Link to="/privacy" target="_blank" className="text-signal-blue hover:underline">
                      {t('register.privacy_link', 'Privacy Policy (Datenschutz)')}
                    </Link>{' '}
                    {t('register.accept_privacy_suffix', 'and consent to the processing of my personal data as described therein')}
                    <span className="text-red-400">*</span>
                  </Label>
                </div>
              </div>

              <p className="text-xs text-slate-600 mt-2">
                <span className="text-red-400">*</span>{' '}
                {t('register.required_fields_note', 'Required fields. By registering, you confirm that you are at least 18 years old.')}
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading || !formData.acceptTerms || !formData.acceptPrivacy}
              className={`w-full text-white rounded-sm py-5 ${
                formData.role === 'OWNER'
                  ? 'bg-military-gold hover:bg-military-gold/90 disabled:bg-military-gold/50'
                  : 'bg-signal-blue hover:bg-signal-blue-hover disabled:bg-signal-blue/50'
              }`}
              data-testid="register-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('register.creating_account', 'Creating Account...')}
                </>
              ) : (
                submitLabel
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-slate-600 text-sm">
              {t('register.already_have_account', 'Already have an account?')}{' '}
              <Link
                to="/login"
                className="text-signal-blue hover:text-signal-blue-hover"
                data-testid="login-link"
              >
                {t('register.sign_in', 'Sign in')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
